import { AuthConsumerRenderProps, withAuthContext } from 'auth/AuthContext';
import { CancelTokenSource } from 'axios';
import { BillIconRequest, getBillIcon } from 'components/bill/utils/billUtils';
import DropZone from 'components/dropzone/DropZone';
import CheckBox, { CheckboxSc } from 'components/final-form/CheckBox';
import ErrorMessage from 'components/final-form/ErrorMessage';
import Input from 'components/final-form/Input';
import FormButtonsRow from 'components/layout/FormButtonsRow';
import FormContent from 'components/layout/FormContent';
import StyledErrorMessage from 'components/layout/StyledErrorMessage';
import VirtualizedTable from 'components/layout/VirtualizedTable';
import LoaderComponent from 'components/LoaderComponent';
import PdfModal from 'components/pdf-modal/PdfModal';
import { FormApi } from 'final-form';
import arrayMutators from 'final-form-arrays';
import createDecorator from 'final-form-calculate';
import moment from 'moment';
import React, { Component, Fragment } from 'react';
import { FileError, FileRejection } from 'react-dropzone';
import { Field, Form as FinalForm, FormRenderProps } from 'react-final-form';
import { MessageSourceProps, withMessages } from 'react-message-source';
import { RouteComponentProps } from 'react-router';
import { Button, Header, Progress } from 'semantic-ui-react';
import axios from 'services/http';
import {
  automaticPayBills,
  getAutomaticPaymentProgressResult,
  getAutomaticPaymentProofListPdfUrl,
  getAutomaticPaymentRunStatus,
  getExecutingAutomaticPaymentRunStatus,
  importAutomaticPayments
} from 'services/paymentServices';
import styled from 'styled-components';
import { AutomaticPaymentStatus, BillStatus, BillType } from 'ts-types/api.enums';
import {
  AutomaticPaymentRunDto,
  BillPaymentDto,
  BillPaymentRequest,
  BillPaymentResponse,
  BillPaymentSearchResponse,
  ImportAutomaticPaymentRequest,
  ImportAutomaticPaymentResponse
} from 'ts-types/api.types';
import { errorUtils } from 'utils/errorUtils';
import { numberFormatter } from 'utils/fomattingUtils';
import { isValueValidNumber, mustBeNumber } from 'utils/validatorUtils';
import { withRouterWorkaround } from 'utils/workaroundUtils';

const StyledTable = styled(VirtualizedTable)`
  .ReactVirtualized__Table__Grid {
    outline: none;
    border: 1px solid rgba(34, 36, 38, .15);
    border-top: none;
    border-radius: 0 0 .28571429rem .28571429rem;
  }

  .ReactVirtualized__Table__row {
    border-left: none;
  }

  .ReactVirtualized__Table__headerColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__headerColumn:last-child {
    border-right: none;
  }

  .ReactVirtualized__Table__rowColumn .align-right {
    text-align: right;
  }

  .ReactVirtualized__Table__rowColumn {
    height: 100%;
    border-right: 1px solid rgba(34, 36, 38, .15);
    padding-top: 0.85rem;
  }

  .ReactVirtualized__Table__rowColumn:last-child {
    border-right: none;
  }

  & .evenRow:hover, .oddRow:hover {
    background: rgba(0, 0, 0, .05);
  }
`;

const StyledForm = styled.form`
  flex: 1 1 auto;
  display: flex;
  flex-flow: column;

  .automatic-payment-form-content {
    display: flex;
    flex-direction: column;
  }

  & .ui.red.message {
    white-space: pre-line;
  }
`;

const BillsContainer = styled.div`
  flex: 1 1 auto;
  min-height: 325px;
`;

const FlexRowContainer = styled.div`
  display: flex;
  flex-flow: row wrap;
  height: 100%;
  column-gap: 1rem;
  row-gap: 4rem;
`;

const FlexRow = styled.div`
  flex: 1 1 auto;

  &.marginBottom {
    margin-bottom: 1rem;
  }

  &.bills-table-pane {
    display: flex;
    flex-direction: column;

    h4.ui.header {
      margin-top: 0.95rem;
    }
  }
`;

const FullWidthRow = styled.div`
  flex: 0 0 auto;

  &.marginBottom {
    margin-bottom: 1rem;
  }
`;

const RightWidthRow = styled.div`
  flex: 0 0 auto;
  min-width: 28rem;

  .details-label {
    font-weight: bold;
    font-size: 1.1rem;
    margin-bottom: 0.35rem;
  }

  .details-label:not(:first-child) {
    margin-top: 1.5rem;
  }

  .details-value {
    margin-bottom: 0.5rem;
  }

  .alignLeft {
    float: left;
  }

  .alignRight {
    float: right;
  }

  .clearBoth {
    clear: both;
  }
`;

const StyledFieldContainer = styled.div`
  margin-bottom: 0.5rem;

  .label {
    display: inline-block;
    width: 7rem;
  }

  .field {
    display: inline-block;
    width: 60%;
  }

  .checkbox {
    min-width: 25%;
  }
`;

const StyledDropZoneContainer = styled.div`
  width: 25%;
  display: inline-block;
  vertical-align: top;
  margin-right: 2rem;
`;

const StyledTotalValuesContainer = styled.div`
  width: 60%;
  display: inline-block;

  & .label {
    width: 300px;
    display: inline-block;
  }

  & .total {
    width: 50px;
    text-align: right;
    color: rgb(48 145 218);
    display: inline-block;
  }

  & .amount {
    width: 160px;
    text-align: right;
    color: green;
    display: inline-block;
  }
`;

const PaymentProgressDiv = styled.div`
  display: inline-block;
  margin-left: 1.2rem;

  .progress-count-label {
    margin-left: 0.375rem;
  }

  .progress-count {
    display: inline-block;
    width: 10rem;
    margin-left: 0.75rem;

    .ui.progress:last-child {
      margin: 0 0 -0.5rem;
    }
  }
`;

const errorMessageStyle = {
  marginBottom: "1rem"
};

const activeRowStyle = {
  background: "#e0e0e0",
  color: "rgba(0,0,0,.87)",
  boxShadow: "0 0 0 rgba(0,0,0,.87) inset"
};

const errorRowStyle = {
  color: "#9f3a38"
};

const numberColumnStyle = {
  floating: "right",
  paddingRight: "0.5rem"
};

interface ExtendedBillSearchResponse extends BillPaymentSearchResponse {
  //book: boolean
  //bookBill: boolean,
  currentPaidAmount: number
}

interface FinalFormData {
  bills: Array<ExtendedBillSearchResponse>,
  selected?: ExtendedBillSearchResponse,
  paymentDate: string,
  proofList: boolean
}

interface Props extends MessageSourceProps, AuthConsumerRenderProps, RouteComponentProps {
  bills: Array<BillPaymentSearchResponse>,
  billsLoading: boolean,
  onFilterBills: (importResponse: ImportAutomaticPaymentResponse) => void,
  onBillsLoadingChange: (billsLoading: boolean) => void,
  resetBills: () => void
}

interface State {
  paymentPdfData?: BillPaymentResponse,
  paymentPdfModalOpen: boolean,
  cancelTokenSource: CancelTokenSource,
  formData: Partial<FinalFormData>,
  isProofListed: boolean,
  paymentProgress?: AutomaticPaymentRunDto,
  submitInProgress: boolean,
  errorMessage?: string
}

class AutomaticPaymentTableView extends Component<Props, State> {

  constructor(props: Props) {
    super(props);

    const cancelTokenSource = axios.CancelToken.source();

    const {options: {workDate}} = this.props;

    const formData: Partial<FinalFormData> = {
      paymentDate: workDate
    };

    this.state = {
      paymentPdfModalOpen: false,
      isProofListed: false,
      cancelTokenSource: cancelTokenSource,
      formData: formData,
      submitInProgress: false
    }

    this.checkPaymentProgress();
  }

  componentDidUpdate({billsLoading: prevBillsLoading}: Readonly<Props>, prevState: Readonly<State>, snapshot?: any): void {

    const {formData} = this.state;
    const {bills, billsLoading} = this.props;

    if (prevBillsLoading != billsLoading && !billsLoading && bills && bills.length > 0) {

      const mappedBills: Array<ExtendedBillSearchResponse> = bills.map(bill => {

        //const calculatedPaidAmount = ((bill.amount + bill.reminderSurcharge) - bill.paidAmount).toFixed(2);

        return {
          ...bill,
          //book: bill.book,
          //bookBill: bill.bookBill,
          currentPaidAmount: bill.newPaidAmount
        }
      });

      this.setState({
        formData: {
          ...formData,
          bills: this.flattenBills(mappedBills),
          selected: undefined
        }
      })
    }
  }

  openPaymentPdfModal = (paymentResponse: BillPaymentResponse) => {
    this.setState({
      paymentPdfModalOpen: true,
      paymentPdfData: paymentResponse,
      submitInProgress: false
    })
  };

  closePaymentPdfModal = () => {
    const {isProofListed} = this.state;

    if (!isProofListed) {
      this.props.resetBills();
    } else {
      this.setState({
        isProofListed: false,
        paymentPdfModalOpen: false,
        paymentPdfData: undefined
      });
    }
  };

  getPaymentProofListPdfUrl = (response: BillPaymentResponse) => {
    return getAutomaticPaymentProofListPdfUrl(response.pdfId, response.pdfName);
  }

  modifyBill = (book: boolean, foundBillIndex: number, bills: Array<ExtendedBillSearchResponse>): Partial<FinalFormData> => {

    if (foundBillIndex != -1 && book != undefined) {
      const foundBill = bills[foundBillIndex];

      const modifiedBills = Array.from(bills);

      modifiedBills[foundBillIndex] = {
        ...foundBill
        //bookBill: book ? foundBill.paidAmount + foundBill.currentPaidAmount >= foundBill.amount : false
      };

      return {
        bills: modifiedBills
      }
    }

    return {};
  };

  bookDecorator = createDecorator({
    field: /^bills\[.*]\.book$/,
    updates: (...params) => {
      // this is using UpdatesByName different than UpdatesForAll used in the other cases or something els it is.

      const book: boolean = params[0];
      const {bills} = params[2] as FinalFormData;
      const {bills: oldBills} = params[3] as FinalFormData;

      const foundModifiedIndex = bills
          .findIndex((bill: ExtendedBillSearchResponse, index: number) => bill.book != oldBills[index].book);

      return this.modifyBill(book, foundModifiedIndex, bills);
    }
  });

  // TODO OC: remove this its not used for automatic payment
  showOnlyBookedDecorator = createDecorator({
    field: "showOnlyBooked",
    updates: (...params) => {
      // this is using UpdatesByName different than UpdatesForAll used in the other cases or something els it is.

      const showOnlyBooked = params[0];
      const {bills} = params[2] as FinalFormData;

      const {bills: oldBills} = this.props;

      if (showOnlyBooked) {
        return {
          bills: bills.filter((bill: ExtendedBillSearchResponse) => bill.book),
          selected: undefined
        }
      }

      const bookedBillsMap: Map<string, ExtendedBillSearchResponse> = new Map<string, ExtendedBillSearchResponse>();

      bills.forEach(bill => {
        bookedBillsMap.set(bill.billId, bill);
      });

      const mappedOldBills = oldBills.map(oldBill => {

        const foundBill = bookedBillsMap.get(oldBill.billId);

        //const calculatedPaidAmount = foundBill && foundBill.currentPaidAmount ?
        //    foundBill.currentPaidAmount : ((oldBill.amount + oldBill.reminderSurcharge) - oldBill.paidAmount).toFixed(2);
        const calculatedPaidAmount = foundBill && foundBill.currentPaidAmount ?
            foundBill.currentPaidAmount : oldBill.newPaidAmount;

        return {
          ...oldBill,
          //book: foundBill && foundBill.book,
          //bookBill: foundBill && foundBill.bookBill,
          currentPaidAmount: calculatedPaidAmount
        }
      });

      return {
        bills: mappedOldBills,
        selected: undefined
      }
    }
  });

  mapToExtendedBillSearchResponse = (bills: Array<BillPaymentSearchResponse>): Array<ExtendedBillSearchResponse> => {
    return bills
        .map(bill => ({
          ...bill,
          //book: false,
          //bookBill: false,
          currentPaidAmount: bill.newPaidAmount
        }));
  }

  flattenBills = (bills: Array<ExtendedBillSearchResponse>): Array<ExtendedBillSearchResponse> => {
    return bills
        .map(bill => [bill, ...this.mapToExtendedBillSearchResponse(bill.collectiveBillParts)])
        .flat(1);
  }

  onDropAccepted = (acceptedFiles: File[], values: any) => {
    const {formData, cancelTokenSource} = this.state;
    const {onBillsLoadingChange, onFilterBills} = this.props;

    this.setState({
      formData: {...formData}
    });

    onBillsLoadingChange(true);

    const request: ImportAutomaticPaymentRequest = {files: acceptedFiles}
    importAutomaticPayments(request, cancelTokenSource)
        .then(response => {
          onFilterBills(response);
        })
        .catch((e) => {
          this.handleError(e.response.data);
        })
        .finally(() => onBillsLoadingChange(false));
  };

  onDropRejected = (rejections: FileRejection[]) => {
    let errors: FileError[] = [];
    rejections.forEach((rejection) => {
      errors = rejection.errors;
    })

    errors.forEach((error) => {
      this.setErrorMessage(error.message);
    });
  };

  printProofList = (values: Partial<FinalFormData>) => {
    const newValues = {...values, proofList: true};
    this.setState({
      isProofListed: true
    })
    this.handleSubmit(newValues);
  };

  isBillSelectable = (bill: BillPaymentSearchResponse) => {
    return bill.date !== null
        && bill.status !== BillStatus.STORNO
        && bill.status !== BillStatus.PAID_CLOSED
        && bill.type !== BillType.PART_OF_COLLECTIVE;
  };

  checkPaymentProgress = async () => {
    const {getMessage} = this.props;
    try {
      const paymentProgress = await getExecutingAutomaticPaymentRunStatus(this.state.cancelTokenSource);
      if (paymentProgress) {
        this.setState({
          paymentProgress
        });
      }

      if (paymentProgress && AutomaticPaymentStatus.IN_PROGRESS === paymentProgress.status) {
        this.setErrorMessage(getMessage("error.AUTOMATIC_PAYMENT_ALREADY_IN_PROGRESS"))
      }

    } catch (e) {
      this.handleError(e.response.data);
    }
  };

  trackPaymentInProgress = () => {
    const {paymentProgress} = this.state;
    const {currentUser, getMessage} = this.props;
    getAutomaticPaymentRunStatus(paymentProgress!.id, this.state.cancelTokenSource)
        .then(async response => {
          this.setState({paymentProgress: response});
          const status = response.status;
          if (AutomaticPaymentStatus.IN_PROGRESS === status) {
            setTimeout(() => this.trackPaymentInProgress(), 2000);
          } else if (AutomaticPaymentStatus.ERROR === status) {
            this.setErrorMessage(getMessage("error.general"));
          } else if (AutomaticPaymentStatus.FINISHED === status) {
            if (response.pdfId && currentUser!.id === response.userId) {
              try {
                const result = await getAutomaticPaymentProgressResult(response.pdfId, this.state.cancelTokenSource);
                if (result) {
                  this.openPaymentPdfModal(result);
                }
              } catch (e) {
              }
            }
          }
        });
  }

  setErrorMessage = (errorMessage?: string) => {
    this.setState({
      errorMessage
    });
  };

  handleError = (error: any): void => {
    const {getMessage} = this.props;

    const status = error.status;
    const exception: string = error.exception;
    if (status === 500 && exception.includes("MultipartException")) {
      this.setErrorMessage(getMessage("error.fileSizeExceeded"))
      return;
    }

    const errorCode = error.errorCode;

    const knownErrors: Array<String> = [
      errorUtils.automaticPaymentAlreadyInProgress
    ];

    const violations: Array<any> = error.violations;

    if (violations && violations.length > 0) {
      violations.forEach(violation => {
        if (knownErrors.includes(violation.errorCode)) {
          this.setErrorMessage(getMessage(`error.${violation.errorCode}`));
        }
      });
    } else {
      if (knownErrors.includes(errorCode)) {
        this.setErrorMessage(getMessage(`error.${errorCode}`));
      }
    }

    this.setErrorMessage(getMessage('error.general'));
  }

  handleSubmit = async (values: Partial<FinalFormData>) => {

    const {bills, paymentDate, proofList} = values;

    if (bills && bills.length > 0) {

      const {cancelTokenSource} = this.state;
      const {options: {workDate}, getMessage, bills: gottenBills, currentUser} = this.props;

      const bookedBills = bills.filter(bill => bill.book);

      if (bookedBills.length > 0) {

        const hasInvalidPrice = bookedBills.some(bill => !isValueValidNumber(bill.currentPaidAmount));

        if (hasInvalidPrice) {
          this.setErrorMessage(getMessage("error.bookedBillInvalidPrice"));
        }

        this.setState({
          submitInProgress: true
        });

        const billPayments: Array<BillPaymentDto> = bills
            .map(bill => {

              const foundBill = gottenBills.find(gottenBill => gottenBill.billId == bill.billId);

              let collectiveBillPartIds: Array<number> = [];
              if (foundBill && foundBill.collectiveBillParts) {
                collectiveBillPartIds = foundBill
                    .collectiveBillParts
                    .map(collectiveBillPart => parseInt(collectiveBillPart.billId.replace("*", "")));
              }

              return {
                billId: parseInt(bill.billId.replace("*", "")),
                paidAmount: bill.currentPaidAmount,
                bookBill: bill.book && bill.bookBill,
                book: bill.book,
                collectiveBillPartIds: collectiveBillPartIds,
                paymentDate: bill.newPaymentDate,
                importDebtorName: bill.debtorName
              }
            });

        const billPaymentRequest: BillPaymentRequest = {
          paymentDate: paymentDate ? paymentDate : workDate,
          billPayments: billPayments,
          proofList: !!proofList,
          userId: currentUser!.id,
          mandateId: currentUser!.userConfig.mandateId,
          automaticImport: true
        };

        this.setState({
          paymentProgress: undefined
        });

        await automaticPayBills(billPaymentRequest, cancelTokenSource)
            .then(response => {
              if (AutomaticPaymentStatus.IN_PROGRESS === response.status) {
                this.setState({paymentProgress: response});
                this.trackPaymentInProgress();
              }
            })
            .catch((e) => {
              this.handleError(e.response.data);
            });
      } else {
        return {
          error: getMessage("error.noBillsToBePaid")
        };
      }
    }
  };

  bookCellRenderer = (data: any) => {

    const {rowIndex, rowData} = data;

    return (
        <Field
            name={`bills[${rowIndex}].book`}
            component={CheckBox}
            disabled={rowData.date === null || rowData.status == BillStatus.STORNO
                || rowData.status == BillStatus.PAID_CLOSED
                || rowData.type == BillType.PART_OF_COLLECTIVE}
        />
    )
  };

  billIdCellRenderer = (data: any) => {
    const rowData: ExtendedBillSearchResponse = data.rowData;

    // invalid bill
    if (rowData.billId > "0") {
      return <div>{rowData.billId}</div>
    } else {
      return <div></div>
    }

  };

  debtorCellRenderer = (data: any) => {
    const rowData: ExtendedBillSearchResponse = data.rowData;

    return <div>{rowData.debtorName}</div>

  };

  billStatusCellRenderer = (data: any) => {

    const rowData: ExtendedBillSearchResponse = data.rowData;

    const billIconRequest: BillIconRequest = {
      hasReminders: rowData.hasBillReminders,
      status: rowData.status as BillStatus,
      type: rowData.type as BillType,
      cashBill: rowData.cashBill
    };
    // invalid bill
    if (rowData.date === null) {
      return <div></div>
    } else {
      return <div>{getBillIcon(billIconRequest)}</div>
    }

  };

  billDateCellRenderer = (data: any) => {

    if (data.rowData.date === null) {
      return <div></div>;
    } else {
      return <div>{moment(data.rowData.date).format("DD.MM.YYYY")}</div>;
    }
  }

  paymentDateCellRenderer = (data: any) => {

    if (data.rowData.newPaymentDate === null) {
      return <div></div>;
    } else {
      return <div>{moment(data.rowData.newPaymentDate).format("DD.MM.YYYY")}</div>;
    }
  }

  valueCellRenderer = (data: any) => {
    const rowData: ExtendedBillSearchResponse = data.rowData;
    const columnIndex: number = data.columnIndex;

    if (columnIndex == 4) {
      return <div className="align-right" style={numberColumnStyle}>{numberFormatter.format(rowData.amount)}</div>
    }

    const {currentPaidAmount, book, paidAmount} = rowData;

    // TODO OC: WTF is this HERE !!!!!!!!!!!!
    let calculatedPaidAmount = rowData.paidAmount;
    if (book && isValueValidNumber(currentPaidAmount)) {
      calculatedPaidAmount = +currentPaidAmount + paidAmount;
    } else if (rowData.status === "STORNO" || rowData.status === "PAID_CLOSED") {
      calculatedPaidAmount = rowData.newPaidAmount;
    }

    return <div className="align-right" style={numberColumnStyle}>{numberFormatter.format(calculatedPaidAmount)}</div>
  };

  selectHeaderColumn = (form: FormApi) => {

    const values = form.getState().values;
    let bills: ExtendedBillSearchResponse[] = values.bills ? [...values.bills] : [];
    let countBookableBills = 0;
    const bookableBills = bills.filter((b: BillPaymentSearchResponse) => this.isBillSelectable(b));
    countBookableBills = bookableBills.length;
    const countBookedBills = bookableBills.filter((b: ExtendedBillSearchResponse) => b.book).length;
    const indeterminate = countBookedBills && countBookedBills < countBookableBills;
    const noBookedBills = !countBookedBills;
    const allBillsBooked = countBookableBills && countBookedBills === countBookableBills;

    return (
        <span>
          <CheckboxSc
              style={{display: 'inline-block', marginTop: '0.1rem'}}
              checked={!!allBillsBooked}
              indeterminate={!!indeterminate}
              onChange={
                () => {
                  if (noBookedBills || indeterminate) {
                    bills = bills.map(bill => {
                      if (this.isBillSelectable(bill)) {
                        return {...bill, book: true}
                      }
                      return bill;
                    });
                  }

                  if (allBillsBooked) {
                    bills = bills.map(bill => {
                      if (this.isBillSelectable(bill)) {
                        return {...bill, book: false}
                      }
                      return bill;
                    });
                  }

                  form.change("bills", bills);
                }
              }
              disabled={!bills.length && !countBookableBills}
          />
          <div style={{display: 'inline-block', verticalAlign: 'top'}}>{this.props.getMessage("book")}</div>
        </span>
    )
  };

  billsRowGetter = (bills: Array<ExtendedBillSearchResponse>) => ({index}: any) => {

    if (bills[index]) {
      Object.assign(bills[index], {index: index + 1});

      return bills[index];
    }

    return {
      index: index + 1
    }
  };

  billsRowRenderer = (form: FormApi, bills: Array<ExtendedBillSearchResponse>) => (
      {className, columns, index, key, style}: any) => {

    const a11yProps = {"aria-rowindex": index + 1};

    const {selected}: Partial<FinalFormData> = form.getState().values;

    let styles = {...style};

    const {billId, book, currentPaidAmount} = bills[index];

    if (selected && billId == selected.billId) {
      styles = {
        ...styles,
        ...activeRowStyle
      }
    }

    if (book) {
      if (isValueValidNumber(currentPaidAmount)) {
        styles.fontWeight = "bold";
      } else {
        styles = {
          ...styles,
          ...errorRowStyle
        }
      }
    }

    return (
        <div
            {...a11yProps}
            className={className}
            key={key}
            role="row"
            style={styles}
            onClick={() => form.change("selected", bills[index])}
        >
          {columns}
        </div>
    );
  };

  render(): React.ReactNode {
    const {formData, paymentPdfModalOpen, paymentPdfData} = this.state;
    return (
        <React.Fragment>
          <FinalForm
              onSubmit={this.handleSubmit}
              initialValues={formData}
              mutators={{
                ...arrayMutators
              }}
              decorators={[this.bookDecorator, this.showOnlyBookedDecorator]}
              subscription={{values: true, pristine: true, submitting: true}}
              render={this.renderForm}
          />
          {
              paymentPdfModalOpen &&
              <PdfModal
                  modalOpen={paymentPdfModalOpen}
                  onCloseModal={this.closePaymentPdfModal}
                  pdfResponses={[{...paymentPdfData}]}
                  getPdfUrl={this.getPaymentProofListPdfUrl}
              />
          }
        </React.Fragment>
    );
  }

  renderForm = ({submitting, values, form, handleSubmit}: FormRenderProps): React.ReactNode => {

    const {submitInProgress, paymentProgress, errorMessage} = this.state;
    const {bills = [], selected, showOnlyBooked} = values;
    const {getMessage, billsLoading, getMessageWithNamedParams, history} = this.props;

    let bookedBills = 0;
    let paidAmount = 0;
    let amount = 0;
    let selectedIndex = -1;
    let billClosed = false;
    let invalidBill = false;
    let billType = BillType.NORMAL;
    let selectedHasPatient = false;

    if (bills.length > 0) {

      if (selected) {
        selectedIndex = bills.findIndex((bill: ExtendedBillSearchResponse) => bill.billId == selected.billId);

        if (selected.date === null) {
          invalidBill = true;
        }

        if (selectedIndex != -1) {
          billClosed = [BillStatus.PAID_CLOSED, BillStatus.STORNO].includes(selected.status);

          const {book, currentPaidAmount, paidAmount: billPaidAmount, type, newPaidAmount} = bills[selectedIndex];
          const {patientName, patientFirstName, patientCity, patientBirthDate} = bills[selectedIndex];

          billType = type as BillType;

          if (!billClosed && book && isValueValidNumber(currentPaidAmount)) {
            paidAmount = +currentPaidAmount + billPaidAmount;
          } else {
            paidAmount = billPaidAmount;
          }
          if (billClosed) {
            paidAmount = newPaidAmount;
          }
          selectedHasPatient = patientName || patientFirstName || patientCity || patientBirthDate;
        }
      }

      const billsToBePaid = bills.filter((bill: ExtendedBillSearchResponse) => bill.book);

      bookedBills = billsToBePaid.length;
      amount = billsToBePaid
          .filter((bill: ExtendedBillSearchResponse) => bill.currentPaidAmount && isValueValidNumber(bill.currentPaidAmount))
          .map((bill: ExtendedBillSearchResponse) => +bill.currentPaidAmount)
          .reduce((amount1: number, amount2: number) => amount1 + amount2, 0);
    }

    const selectableBills: Array<any> = [];
    bills.forEach((bill: ExtendedBillSearchResponse) => {
      if (bill.date !== null
          && bill.status !== BillStatus.STORNO
          && bill.status !== BillStatus.PAID_CLOSED
          && bill.type !== BillType.PART_OF_COLLECTIVE) {
        selectableBills.push(bill)
      }
    });

    const totalNotBookedBills = selectableBills.length - bookedBills;
    const notBookedBillsAmount = selectableBills
        .filter((bill: ExtendedBillSearchResponse) => bill.currentPaidAmount && isValueValidNumber(bill.currentPaidAmount) && !bill.book)
        .map((bill: ExtendedBillSearchResponse) => +bill.currentPaidAmount)
        .reduce((amount1: number, amount2: number) => amount1 + amount2, 0);

    const paidBills: Array<any> = [];
    bills.forEach((bill: ExtendedBillSearchResponse) => {
      if (bill.status === BillStatus.PAID_CLOSED) {
        paidBills.push(bill)
      }
    });
    const paidBillsAmount = paidBills
        .filter((bill: ExtendedBillSearchResponse) => bill.currentPaidAmount && isValueValidNumber(bill.currentPaidAmount))
        .map((bill: ExtendedBillSearchResponse) => +bill.currentPaidAmount)
        .reduce((amount1: number, amount2: number) => amount1 + amount2, 0);

    const missingBills: Array<any> = [];
    bills.forEach((bill: ExtendedBillSearchResponse) => {
      if (bill.date === null) {
        missingBills.push(bill)
      }
    });
    const missingBillsAmount = missingBills
        .filter((bill: ExtendedBillSearchResponse) => bill.currentPaidAmount && isValueValidNumber(bill.currentPaidAmount))
        .map((bill: ExtendedBillSearchResponse) => +bill.currentPaidAmount)
        .reduce((amount1: number, amount2: number) => amount1 + amount2, 0);

    const disabledBtn = bookedBills < 1;
    const disabledProofList = bills.length < 1;

    const paymentProgressFinalizing = paymentProgress
        && paymentProgress.processedBills === paymentProgress.totalBills;

    return (
        <StyledForm onSubmit={handleSubmit}>
          <FormContent className="automatic-payment-form-content">
            {errorMessage
                && (
                    <StyledErrorMessage onDismiss={() => this.setErrorMessage()}>
                      {errorMessage}
                    </StyledErrorMessage>
                )
            }
            <FullWidthRow>
              <Field
                  name="error"
                  component={ErrorMessage}
                  style={errorMessageStyle}
              />
            </FullWidthRow>
            <FullWidthRow className="marginBottom">
              <Header>{getMessage("automaticPayment.selectFile")}</Header>
              <div>
                <StyledDropZoneContainer>
                  <DropZone
                      maxSize={21485760}
                      multiple
                      message={getMessage("automaticPayment.selectCAMTFile")}
                      onDropAccepted={(acceptedFiles: File[]) => this.onDropAccepted(acceptedFiles, values)}
                      onDropRejected={this.onDropRejected}
                      accept=".xml"
                  />
                </StyledDropZoneContainer>
                <StyledTotalValuesContainer>
                  <FlexRow className={'marginBottom'}>
                    <Header>
                      <span className={'label'}>{getMessage('automaticPayment.totalBooked')}:</span>
                      <span className={'total'}>{bookedBills}</span>
                      <span className={'amount'}>{numberFormatter.format(amount)} CHF</span>
                    </Header>
                  </FlexRow>
                  <FlexRow className={'marginBottom'}>
                    <Header>
                      <span className={'label'}>{getMessage('automaticPayment.totalNotBooked')}:</span>
                      <span className={'total'}> {totalNotBookedBills}</span>
                      <span className={'amount'}>{numberFormatter.format(notBookedBillsAmount)} CHF</span>
                    </Header>
                  </FlexRow>
                  <FlexRow className={'marginBottom'}>
                    <Header>
                      <span className={'label'}>{getMessage('automaticPayment.totalAlreadyPaid')}:</span>
                      <span className={'total'}> {paidBills.length}</span>
                      <span className={'amount'}>{numberFormatter.format(paidBillsAmount)} CHF</span>
                    </Header>
                  </FlexRow>
                  <FlexRow className={'marginBottom'}>
                    <Header>
                      <span className={'label'}>{getMessage('automaticPayment.totalMissingBills')}</span>
                      <span className={'total'}> {missingBills.length}</span>
                      <span className={'amount'}>{numberFormatter.format(missingBillsAmount)} CHF</span>
                    </Header>
                  </FlexRow>
                </StyledTotalValuesContainer>
              </div>
            </FullWidthRow>
            <Header>{getMessage("automaticPayment.totalBills")}:
              <span style={{color: 'rgb(48 145 218)'}}> {bills.length} </span>
            </Header>

            {/*<FullWidthRow className="marginBottom">*/}
            {/*  <FlexRowContainer>*/}
            {/*    <Header>*/}
            {/*      {getMessage("automaticPayment.transactions")}*/}
            {/*    </Header>*/}
            {/*    /!*<Field*/}
            {/*          name="showOnlyBooked"*/}
            {/*          component={CheckBox}*/}
            {/*          label={getMessage("bills.showOnlyBooked")}*/}
            {/*          disabled={bills.length == 0 && !showOnlyBooked}*/}
            {/*      />*!/*/}
            {/*  </FlexRowContainer>*/}
            {/*</FullWidthRow>*/}
            <FlexRow>
              <FlexRowContainer>
                <FlexRow className="bills-table-pane">
                  <BillsContainer>
                    {billsLoading
                        ? <LoaderComponent
                            message={`${getMessage("loading")} ${getMessage("automaticPayment.transactions").toLowerCase()}`}
                        />
                        : this.renderTable(bills, form)}
                  </BillsContainer>
                  <Header as="h4" textAlign="right">
                    {
                      getMessageWithNamedParams("bills.numberOfBookedBills", {
                        bookedBills: bookedBills,
                        amount: numberFormatter.format(amount)
                      })
                    }
                  </Header>
                </FlexRow>
                <RightWidthRow>
                  <div className="details-label">{getMessage("patient")}</div>
                  {
                      selectedHasPatient
                      && <div className="details-value">
                        <div>{`${selected.patientName}, ${selected.patientFirstName}`}</div>
                        <div>{`${selected.patientCity}, ${selected.patientBirthDate}`}</div>
                      </div>
                  }

                  <div className="details-label">{getMessage("guarantor")}</div>
                  {
                      selected && selected.guarantorDescription
                      &&
                      <div className="details-value">
                        <div>{`${selected.guarantorDescription}`}</div>
                        <div>{`${selected.guarantorZip}, ${selected.guarantorCity}`}</div>
                      </div>
                  }

                  <div className="details-label">{getMessage("payment")}</div>
                  {
                      selectedIndex != -1 && !invalidBill && !billClosed && billType != BillType.PART_OF_COLLECTIVE
                      && <Fragment>
                        <StyledFieldContainer>
                          <div className="label"></div>
                          <Field
                              name={`bills[${selectedIndex}].book`}
                              component={CheckBox}
                              label={getMessage("book")}
                          />
                          <Field
                              name={`bills[${selectedIndex}].bookBill`}
                              component={CheckBox}
                              label={getMessage("bookBill")}
                              disabled={!bills[selectedIndex].book}
                          />
                        </StyledFieldContainer>
                        <StyledFieldContainer>
                          <div className="label">{getMessage("amount")}:</div>
                          <Field
                              name={`bills[${selectedIndex}].currentPaidAmount`}
                              validate={bills[selectedIndex].book ? mustBeNumber : undefined}
                              component={Input}
                              fluid
                          />
                        </StyledFieldContainer>
                      </Fragment>
                  }

                  <div className="details-label">{getMessage("bill.information")}</div>
                  {
                      selected
                      && <Fragment>
                        <div className="details-value">
                          <div className="alignLeft">{getMessage("bill.amountHeader")}:</div>
                          <div className="alignRight">{numberFormatter.format(selected.amount)}</div>
                          <div className="clearBoth"></div>
                        </div>
                        <div className="details-value">
                          <div className="alignLeft">{getMessage("amountPaid")}:</div>
                          <div className="alignRight">{numberFormatter.format(paidAmount)}</div>
                          <div className="clearBoth"></div>
                        </div>
                        <div className="details-value">
                          <div className="alignLeft">{getMessage("reminderSurcharge")}:</div>
                          <div className="alignRight">{numberFormatter.format(selected.reminderSurcharge)}</div>
                          <div className="clearBoth"></div>
                        </div>
                      </Fragment>
                  }
                </RightWidthRow>
              </FlexRowContainer>
            </FlexRow>
          </FormContent>

          <FormButtonsRow>
            <Button
                compact
                type="button"
                onClick={() => {
                  this.checkPaymentProgress();
                  this.props.resetBills();
                }
                }
                disabled={submitting || submitInProgress}
            >
              {getMessage("button.refresh")}
            </Button>
            <Button compact
                    type="button"
                    secondary
                    disabled={disabledProofList || submitInProgress}
                    onClick={() => this.printProofList(values)}>
              {getMessage("collectiveBill.printProoflist")}
            </Button>
            <Button compact type="submit" primary disabled={submitting || submitInProgress || disabledBtn}>
              {getMessage("book")}
            </Button>
            {
              paymentProgress
              && paymentProgress.status === AutomaticPaymentStatus.IN_PROGRESS
              && (submitting || submitInProgress)
                  ? (
                      <PaymentProgressDiv>
                        {paymentProgressFinalizing && (
                            <span className="progress-label">{getMessage("bill.billingInProgressFinalizing")}</span>
                        )}
                        {!paymentProgressFinalizing && (
                            <>
                              <span className="progress-label">{getMessage("bill.billingInProgress")}</span>
                              <span className="progress-count-label">
                                  ({`${paymentProgress.processedBills} / ${paymentProgress.totalBills}`})
                                </span>
                            </>
                        )}
                        <div className="progress-count">
                          <Progress
                              color="blue"
                              percent={(paymentProgress.processedBills / paymentProgress.totalBills) * 100}
                              active/>
                        </div>
                      </PaymentProgressDiv>
                  )
                  : ''
            }
          </FormButtonsRow>
        </StyledForm>
    )
  };

  renderTable = (bills: Array<ExtendedBillSearchResponse>, form: FormApi): JSX.Element => {

    const {getMessage} = this.props;

    return (
        <StyledTable
            rowGetter={this.billsRowGetter(bills)}
            rowRenderer={this.billsRowRenderer(form, bills)}
            rowCount={bills.length}
            columns={[
              {
                width: 70,
                label: this.selectHeaderColumn(form),
                dataKey: "book",
                cellRenderer: this.bookCellRenderer
              },
              {
                width: 100,
                label: getMessage("bill"),
                dataKey: "status",
                cellRenderer: this.billStatusCellRenderer
              },
              {
                width: 70,
                label: "#",
                dataKey: "billId",
                cellRenderer: this.billIdCellRenderer
              },
              {
                width: 100,
                label: getMessage("bill.date"),
                dataKey: "date",
                cellRenderer: this.billDateCellRenderer
              },
              {
                width: 100,
                label: getMessage("value"),
                dataKey: "amount",
                cellRenderer: this.valueCellRenderer
              },
              {
                width: 400,
                label: getMessage("bill.debtorName"),
                dataKey: "debtorName",
                cellRenderer: this.debtorCellRenderer,
                flexGrow: 1
              },
              {
                width: 100,
                label: getMessage("bill.paymentDate"),
                dataKey: "newPaymentDate",
                cellRenderer: this.paymentDateCellRenderer
              },
              {
                width: 100,
                label: getMessage("paidValue"),
                dataKey: "paidValue",
                cellRenderer: this.valueCellRenderer,
                flexGrow: 1
              }
            ]}
        />
    )
  };
}

const AutomaticPaymentTableViewWrapper =
    withMessages(
        withAuthContext(
            withRouterWorkaround(
                AutomaticPaymentTableView)));

export default AutomaticPaymentTableViewWrapper;
